import * as React from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import useSearchSpring from "@src/hooks/useSearchSpring";
import { trackProductClick, trackProductImpression } from "../../../../js/searchspringBeacon";
import { mobileBreakpoint } from "../../../../entrypoints/theme";
import ExtendedProductTile from "@stories/components/ProductTile/ProductTile";
import PromotionTile from "@stories/ecommerce/PromotionalTile/PromotionTile";
import CollectionToolbar from "@stories/ecommerce/Collection/CollectionToolbar";
import { useState, useEffect, useRef, useCallback } from "react";
import { CloseIcon } from "@src/stories/elements/Icons";


  type PriceFilterValue = {
    low: string | null;
    high: string | null;
  };

  type Filter = {
    filterField: string;
    value: string | PriceFilterValue;
  };



export const Collection = ({ settings }) => {
  const isMobile = useMobileBreakpoint(mobileBreakpoint);
  const [sortLoading, setSortLoading] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState("");
  const [filterLoading, setFilterLoading] = useState(false);
  const [currentView, setCurrentView] = useState("product");
  const prevViewRef = useRef(currentView);
  const loadMoreRef = useRef(null);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [selectedFilterCounts, setSelectedFilterCounts] = useState({});
  const [totalSelectedFilters, setTotalSelectedFilters] = useState(0);


  useEffect(() => {
    if (!selectedFilters) {
      setSelectedFilterCounts({});
      setTotalSelectedFilters(0);
      return;
    }

    const counts = {};
    let total = 0;
    
    const filterPairs = selectedFilters.split('&').filter(Boolean);
    
    filterPairs.forEach(pair => {
      const [key, value] = pair.split('=');
      if (!key || !value) return;
      
      const fieldMatch = key.match(/filter\.(.+?)(?:\.low|\.high)?$/);
      if (fieldMatch) {
        const fieldName = fieldMatch[1];
        
        if (key.includes('.low') || key.includes('.high')) {
          if (key.includes('.low')) {
            counts[fieldName] = (counts[fieldName] || 0) + 1;
            total += 1;
          }
        } else {
          if (value.includes('|')) {
            const valueCount = value.split('|').length;
            counts[fieldName] = (counts[fieldName] || 0) + valueCount;
            total += valueCount;
          } else {
            counts[fieldName] = (counts[fieldName] || 0) + 1;
            total += 1;
          }
        }
      }
    });
    
    setSelectedFilterCounts(counts);
    setTotalSelectedFilters(total);
  }, [selectedFilters]);


const parseSelectedFilters = (): Filter[] => {
    const filtersArray = selectedFilters.split("&").filter(Boolean);
    const parsedFilters: Filter[] = [];
  
    filtersArray.forEach((filter) => {
      const [key, value] = filter.split("=");
      const [, filterField, rangeType] = key.split(".");
  
      if (filterField === "ss_price") {
        let priceFilter = parsedFilters.find(
          (f) => f.filterField === "ss_price"
        ) as Filter | undefined;
  
        if (!priceFilter) {
          priceFilter = {
            filterField: "ss_price",
            value: { low: null, high: null },
          };
          parsedFilters.push(priceFilter);
        }
  
        const priceValue = priceFilter.value as PriceFilterValue;
  
        if (rangeType === "low") {
          priceValue.low = value;
        } else if (rangeType === "high") {
          priceValue.high = value;
        }
      } else {
        parsedFilters.push({
          filterField,
          value: value,
        });
      }
    });
  
    return parsedFilters.map((filter) => {
      if (filter.filterField === "ss_price") {
        const { low, high } = filter.value as PriceFilterValue;
        return {
          filterField: "ss_price",
          value: `$${low} to $${high}`,
        };
      }
      return filter;
    });
  };

  const selectedFiltersArray = parseSelectedFilters();
  
  const clearAllFilters = () => {
    setSelectedFilters("");
    changeFilter && changeFilter({});
  };
  
  const removeFilter = (filterField, value) => {
    const updatedFilters = selectedFilters.split('&')
      .filter(filter => {
        if (filter.startsWith(`filter.${filterField}=`)) {
          const filterValue = filter.split('=')[1];
          if (filterValue === value) {
            return false;
          }
        }
        return true;
      })
      .join('&');
    setSelectedFilters(updatedFilters);
    changeFilter(updatedFilters);
  };


  
  const {
    products,
    filters,
    loading,
    error,
    totalResults,
    loadMoreProducts,
    sortingOptions,
    changeSort,
    changeFilter,
    currentPage = 1
  } = useSearchSpring(settings?.collection);

  // Track product impressions when products load
  useEffect(() => {
    if (products.length > 0 && !loading && settings?.collection) {
      products.forEach((product, index) => {
        trackProductImpression(product, 'collection', {
          position: index + 1,
          collectionHandle: settings.collection.handle,
          collectionTitle: settings.collection.title,
          totalResults,
          page: currentPage
        });
      });
    }
  }, [products, loading, settings?.collection, totalResults, currentPage]);

  if (error) {
    return <div>Error loading collection: {error.message}</div>;
  }
  
  const allProductsLoaded = products.length >= totalResults;

  const pageType = window.location.pathname.includes('/collections/') ? 'collection' : window.location.pathname.includes('/search') ? 'search' : 'other';

  if (error) {
    return <div>Error fetching products: {error.message}</div>;
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading) {
          loadMoreProducts();
        }
      },
      { threshold: 0.5 }
    );

    if (loadMoreRef.current) {

      observer.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
    };
  }, [loading]); 

  const {
    columns_mobile,
    columns_desktop,
    column_gap_mobile,
    column_gap_desktop,
    row_gap_mobile,
    row_gap_desktop,
    id,
  } = settings.section;

  let promo_blocks = settings.promo_blocks;

  if (typeof promo_blocks === 'string') {
    try {
      promo_blocks = JSON.parse(`[${promo_blocks}]`);
      promo_blocks.forEach(block => {
        if (block.description) {
          try {
            block.description = JSON.parse(block.description);
          } catch (e) {
            console.error('Failed to parse block description:', e);
          }
        }
      });

    } catch (e) {
      console.error('Failed to parse promo_blocks:', e);
      promo_blocks = [];
    }
  }

  const renderPromotionBlock = (index) => {
    const promoBlock = promo_blocks?.find(block => block.position_number === index + 1);
    if (promoBlock) {
      return <PromotionTile promotion={promoBlock} />;
    }
    return null;
  };

  useEffect(() => {
    if (currentView !== prevViewRef.current || sortLoading) {
      const productElements = document.querySelectorAll('.product-grid__inner');
      productElements.forEach((el) => {
        el.classList.add('slide-fade-in-element');
        el.addEventListener('animationend', () => {
          el.classList.remove('slide-fade-in-element');
        });
      });
    }

    setSortLoading(false);
    setFilterLoading(false);
    prevViewRef.current = currentView;
  }, [products, currentView, sortLoading]);

  const handleSortChange = (newSortOption) => {
    setSortLoading(true);
    changeSort(newSortOption);
  };

  const handleFilterChange = (filterOption) => {
    setFilterLoading(true);
    changeFilter(filterOption);
  };
  
  const toggleFilterDrawer = () => {
    setIsFilterDrawerOpen(!isFilterDrawerOpen);
    if (!isFilterDrawerOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    setSortLoading(true);
  };

  return (
    <>
   
      <div className="flex flex-col-reverse justify-between lg:mb-2">

        <CollectionToolbar
          sortingOptions={sortingOptions}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          changeSort={handleSortChange}
          initialSortOption={{ field: "updated_at", direction: "asc" }}
          currentView={currentView}
          filters={filters}
          onViewChange={handleViewChange}
          totalResults={totalResults}
          changeFilter={handleFilterChange}
          isFilterDrawerOpen={isFilterDrawerOpen}
          toggleFilterDrawer={toggleFilterDrawer}
          selectedFilterCounts={selectedFilterCounts}
          totalSelectedFilters={totalSelectedFilters}
          clearAllFilters={clearAllFilters}
        />
      </div>

      {(sortLoading || filterLoading || (loading && products.length === 0)) && (
        <div className="flex items-start justify-center my-4 min-h-[3300px] lg:min-h-[2200px]">
          <div className="w-6 h-6 border-t-4 border-b-4 rounded-full border-primary animate-spin"></div>
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-4 lg:mb-[14px]">
        { selectedFiltersArray.length > 0 && 
        <div className="flex items-center mt-0 lg:mt-2.5 mb-2 gap-2 flex-wrap w-full lg:w-[calc(100%-257px)]">
        {selectedFiltersArray.map((filter, index) => (
          <div
            key={index}
            className="flex items-center bg-primary px-[15px] lg:px-2 py-[4px] gap-[8px] h-6"
          >
            {filter.filterField === "ss_price" ? (
              <span className="text-[12px] font-semibold font-body text-secondary">
                {filter.value as string}
              </span>
            ) : <span className="text-[12px] font-semibold font-body text-secondary">
                {typeof filter.value === 'string' ? filter.value.replace(/_/g, " ") : ''}
              </span> 
            }
            <button
              onClick={() =>
                removeFilter(filter.filterField, filter.value)
              }
              className="h-3 w-3 flex justify-center items-center font-semibold text-secondary p-0 "
            >
              <CloseIcon fill={"white"}/>
            </button>
          </div>
        ))}

        {selectedFiltersArray.length > 0 && (
          <button
            onClick={clearAllFilters}
            className="text-[12px] font-normal underline normal-case !h-auto min-h-[auto]  p-0 w-[47px]"
          >
            Clear All
          </button>
        )}
      </div> }

      <div className="flex justify-start" style={{minWidth: selectedFiltersArray.length > 0 ? '257px' : '0'}} >
        <span className="text-sm font-normal text-tertiary font-body lg:ml-auto">
         { isMobile ? `${totalResults} Results`  : `Showing ${totalResults} Products`}
        </span>
      </div>
      </div>

      <div className={`${sortLoading || filterLoading && loading ? 'invisible' : ''}`}>
        <div
          id="product-grid"
          className={`grid grid-cols-${columns_mobile} lg:grid-cols-${columns_desktop} gap-x-[${column_gap_mobile}px] lg:gap-x-[${column_gap_desktop}px] gap-y-[${row_gap_mobile}px] lg:gap-y-[${row_gap_desktop}px] auto-cols-max`}
          data-id={id}
        >
          {products.map((product, index) => (
            <React.Fragment key={product.id}>
              {renderPromotionBlock(index)}
              <div className="product-grid__inner slide-fade-in-element">
                <ExtendedProductTile
                  product={product}
                  badges={{"metaobject_badge": product.metaobject_badge}}
                  tog={product.productTogs}
                  product_swatches={product.colorPatterns}
                  source={'searchspring'}
                  image_placeholder={settings.globals.product_img_placeholder}
                  view={currentView}
                />
              </div>
            </React.Fragment>
          ))}
        </div>

        {!allProductsLoaded && products.length > 0 && (
          <div className="flex justify-center mt-12 mb-6 lg:mb-0">
            <div
              ref={loadMoreRef}
              className="flex items-start justify-center w-auto h-6 transition"
            >
              { loading && <div className="w-6 h-6 border-t-4 border-b-4 rounded-full border-primary animate-spin"></div> }
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Collection;
